/**
 * Приёмка по чек-листу (КЧ-04, КЧ-14): пункты с ответами «соответствует», «не соответствует», «не применимо»,
 * числом с допуском и датой. «Не соответствует» требует фото и сразу создаёт замечание в едином реестре.
 */
import {
  CHECKLIST_ANSWERS,
  CHECKLIST_WORK_KINDS,
  type ChecklistAnswerValue,
  type ChecklistAnswerView,
  type ChecklistInspectionDetail,
  inTolerance,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { CheckCircle2, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { PHOTO_ACCEPT, RemarkBadge, UploadButton } from '../psd/shared';
import { InspectionBadge } from './QualityPage';

const ANSWER_TONE: Record<ChecklistAnswerValue, 'success' | 'danger' | 'neutral'> = {
  ok: 'success',
  fail: 'danger',
  na: 'neutral',
};

export function InspectionPage() {
  const { inspectionId } = useParams({ strict: false }) as { inspectionId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['qa-inspection', inspectionId],
    queryFn: () => api<ChecklistInspectionDetail>(`/quality/inspections/${inspectionId}`),
  });
  useCrumbs(q.data ? [{ label: q.data.number, title: q.data.title }] : null, q.error);
  const refresh = (d?: ChecklistInspectionDetail) => {
    if (d) qc.setQueryData(['qa-inspection', inspectionId], d);
    else qc.invalidateQueries({ queryKey: ['qa-inspection', inspectionId] });
    qc.invalidateQueries({ queryKey: ['qa-inspections'] });
    qc.invalidateQueries({ queryKey: ['remarks'] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const complete = useMutation({
    mutationFn: () =>
      api<ChecklistInspectionDetail>(`/quality/inspections/${inspectionId}/complete`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (d) => {
      refresh(d);
      toast(d.result === 'with_remarks' ? 'Приёмка завершена, замечания выданы' : 'Приёмка завершена');
    },
  });
  const [cancelOpen, setCancelOpen] = useState(false);
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  return (
    <Page>
      <PageHeader
        title={`${d.number}: ${d.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <InspectionBadge i={d} />
            <span>{CHECKLIST_WORK_KINDS[d.workKind]}</span>
          </span>
        }
        actions={
          d.canComplete ? (
            <>
              <Button variant="ghost" size="lg" onClick={() => setCancelOpen(true)}>
                Отменить приёмку
              </Button>
              <Button
                variant="primary"
                size="lg"
                loading={complete.isPending}
                icon={<CheckCircle2 className="size-4" />}
                onClick={() => complete.mutate()}
              >
                Завершить приёмку
              </Button>
            </>
          ) : null
        }
      />
      {complete.isError ? <ErrorBox error={complete.error} className="mb-4" /> : null}
      <Panel className="mb-6">
        <KeyValue
          items={[
            {
              label: 'Проект',
              value: (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: d.projectId }}
                  className="inline-flex min-h-11 items-center underline md:min-h-0"
                >
                  {d.projectCode}
                </Link>
              ),
            },
            { label: 'Объект и место', value: [d.objectName, d.place].filter(Boolean).join(', ') || '—' },
            { label: 'Исполнитель', value: d.responsibleName },
            { label: 'Подрядчик', value: d.contractorName ?? 'Без адресата: замечания внутренние' },
            { label: 'Устраняет замечания', value: d.remarkAssigneeName ?? '—' },
            { label: 'Срок приёмки', value: date(d.dueDate) },
            { label: 'Отвечено пунктов', value: `${d.answered} из ${d.total}` },
            ...(d.completedAt
              ? [{ label: 'Завершена', value: `${dateTime(d.completedAt)}, ${d.completedByName ?? ''}` }]
              : []),
          ]}
        />
      </Panel>
      <ol className="flex flex-col gap-3">
        {d.answers.map((a) => (
          <li key={a.id}>
            <ItemCard d={d} a={a} onChange={refresh} />
          </li>
        ))}
      </ol>
      {cancelOpen ? <CancelDialog id={d.id} onClose={() => setCancelOpen(false)} onDone={refresh} /> : null}
    </Page>
  );
}

function ItemCard({
  d,
  a,
  onChange,
}: {
  d: ChecklistInspectionDetail;
  a: ChecklistAnswerView;
  onChange: (d?: ChecklistInspectionDetail) => void;
}) {
  const [num, setNum] = useState(a.numberValue == null ? '' : String(a.numberValue));
  const [dt, setDt] = useState(a.dateValue ?? '');
  const [comment, setComment] = useState(a.comment ?? '');
  const save = useMutation({
    mutationFn: (body: {
      answer?: ChecklistAnswerValue | null;
      numberValue?: number | null;
      dateValue?: string | null;
      comment?: string | null;
    }) =>
      api<ChecklistInspectionDetail>(`/quality/inspections/${d.id}/items/${a.position}`, {
        method: 'PUT',
        body: { comment: comment.trim() || null, ...body },
      }),
    onSuccess: (r) => {
      onChange(r);
      const next = r.answers.find((x) => x.position === a.position);
      if (next?.answer === 'fail' && next.remark)
        toast(`Создано замечание ${next.remark.number}: приложите фото`, 'info');
    },
  });
  const n = num.trim() === '' ? null : Number(num.replace(',', '.'));
  const live =
    a.answerType === 'number' && n != null && Number.isFinite(n)
      ? inTolerance(n, a.minValue, a.maxValue)
      : null;
  const edit = d.canAnswer;
  return (
    <div
      className={clsx(
        'rounded-lg border bg-surface p-4',
        a.answer === 'fail' ? 'border-danger/60' : 'border-line',
      )}
    >
      <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0">
          <p className="text-[15px] font-medium text-ink [overflow-wrap:anywhere]">
            {a.position}. {a.text}
          </p>
          {a.normRef ? <p className="text-[13px] text-ink-2">Норма: {a.normRef}</p> : null}
          {a.answerType === 'number' ? (
            <p className="text-[13px] text-ink-2">
              Допуск: {a.minValue ?? '—'} … {a.maxValue ?? '—'} {a.unit ?? ''}
            </p>
          ) : null}
        </div>
        {a.answer ? (
          <Badge tone={ANSWER_TONE[a.answer]}>
            {a.answerType === 'number' && a.numberValue != null
              ? `${a.numberValue} ${a.unit ?? ''}: ${CHECKLIST_ANSWERS[a.answer].toLowerCase()}`
              : a.answerType === 'date' && a.dateValue
                ? date(a.dateValue)
                : CHECKLIST_ANSWERS[a.answer]}
          </Badge>
        ) : (
          <Badge>Нет ответа</Badge>
        )}
      </div>
      {edit ? (
        <div className="flex flex-col gap-3">
          {a.answerType === 'conformity' ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(Object.keys(CHECKLIST_ANSWERS) as ChecklistAnswerValue[]).map((k) => (
                <Button
                  key={k}
                  variant={a.answer === k ? 'primary' : 'secondary'}
                  className={clsx('h-11', a.answer === k && k === 'fail' && '!bg-danger !text-white')}
                  loading={save.isPending && save.variables?.answer === k}
                  onClick={() => save.mutate({ answer: k })}
                >
                  {CHECKLIST_ANSWERS[k]}
                </Button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              {a.answerType === 'number' ? (
                <Field
                  label={`Измерено${a.unit ? `, ${a.unit}` : ''}`}
                  className="sm:w-48"
                  hint={
                    live == null ? undefined : live ? 'В допуске' : 'Вне допуска: будет создано замечание'
                  }
                >
                  <Input
                    className="h-11"
                    inputMode="decimal"
                    value={num}
                    onChange={(e) => setNum(e.target.value)}
                  />
                </Field>
              ) : (
                <Field label="Дата" className="sm:w-48">
                  <Input className="h-11" type="date" value={dt} onChange={(e) => setDt(e.target.value)} />
                </Field>
              )}
              <Button
                variant="primary"
                className="h-11"
                loading={save.isPending && save.variables?.answer === undefined}
                disabled={a.answerType === 'number' ? n == null || !Number.isFinite(n) : !dt}
                onClick={() =>
                  save.mutate(a.answerType === 'number' ? { numberValue: n } : { dateValue: dt })
                }
              >
                Записать
              </Button>
              <Button variant="secondary" className="h-11" onClick={() => save.mutate({ answer: 'na' })}>
                Не применимо
              </Button>
            </div>
          )}
          <Field label="Комментарий">
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
          {comment.trim() !== (a.comment ?? '') && a.answer ? (
            <div>
              <Button
                variant="secondary"
                className="h-11"
                loading={save.isPending}
                onClick={() =>
                  save.mutate({ answer: a.answer, numberValue: a.numberValue, dateValue: a.dateValue })
                }
              >
                Сохранить комментарий
              </Button>
            </div>
          ) : null}
          {save.isError ? <ErrorBox error={save.error} /> : null}
        </div>
      ) : a.comment ? (
        <p className="text-[14px] text-ink [overflow-wrap:anywhere]">{a.comment}</p>
      ) : null}
      {a.answer === 'fail' || a.photos.length ? (
        <div className="mt-3 flex flex-col gap-2 border-t border-line pt-3">
          <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
            <Paperclip className="size-4" aria-hidden />
            {a.photos.length ? (
              a.photos.map((f) => (
                <span key={f.id} className="inline-flex items-center gap-1">
                  <a
                    href={`/api/v1/files/${f.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center break-all text-ink underline-offset-4 hover:underline md:min-h-0"
                  >
                    {f.name}
                  </a>
                  {edit ? (
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center px-2 text-danger hover:underline md:min-h-0"
                      onClick={() =>
                        api<ChecklistInspectionDetail>(
                          `/quality/inspections/${d.id}/items/${a.position}/photos/${f.id}`,
                          { method: 'DELETE' },
                        )
                          .then((r) => onChange(r))
                          .catch((e) =>
                            toast(e instanceof Error ? e.message : 'Не удалось убрать фото', 'danger'),
                          )
                      }
                    >
                      Убрать
                    </button>
                  ) : null}
                </span>
              ))
            ) : (
              <span>
                {a.answer === 'fail' ? 'Фото обязательно: без него приёмку не завершить' : 'Фото нет'}
              </span>
            )}
          </div>
          {edit ? (
            <div>
              <UploadButton
                url={`/quality/inspections/${d.id}/items/${a.position}/photos`}
                accept={PHOTO_ACCEPT}
                label="Приложить фото"
                multiple
                onDone={() => onChange()}
              />
            </div>
          ) : null}
        </div>
      ) : edit && a.answer ? (
        <div className="mt-3">
          <UploadButton
            url={`/quality/inspections/${d.id}/items/${a.position}/photos`}
            accept={PHOTO_ACCEPT}
            label="Приложить фото"
            multiple
            onDone={() => onChange()}
          />
        </div>
      ) : null}
      {a.remark ? (
        <p className="mt-3 flex flex-wrap items-center gap-2 text-[14px]">
          Замечание в реестре:
          <Link
            to="/supervision/remarks/$remarkId"
            params={{ remarkId: a.remark.id }}
            className="inline-flex min-h-11 items-center font-medium text-ink underline md:min-h-0"
          >
            {a.remark.number}
          </Link>
          <RemarkBadge status={a.remark.status as never} />
        </p>
      ) : null}
      {a.answeredByName ? (
        <p className="mt-2 text-[12px] text-ink-3">
          Ответил {a.answeredByName}, {dateTime(a.answeredAt)}
        </p>
      ) : null}
    </div>
  );
}

function CancelDialog({
  id,
  onClose,
  onDone,
}: {
  id: string;
  onClose: () => void;
  onDone: (d?: ChecklistInspectionDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api<ChecklistInspectionDetail>(`/quality/inspections/${id}/cancel`, {
        method: 'POST',
        body: { reason: reason.trim() },
      }),
    onSuccess: (d) => {
      onDone(d);
      onClose();
      toast('Приёмка отменена');
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Отменить приёмку"
      description="Выданные по ней замечания остаются в реестре."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Не отменять
          </Button>
          <Button
            variant="danger"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => m.mutate()}
          >
            Отменить приёмку
          </Button>
        </>
      }
    >
      {m.isError ? <ErrorBox error={m.error} className="mb-3" /> : null}
      <Field label="Причина">
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}
